import { ReactElement, useEffect, useState } from "react";
import {
    useUpdateTemplatePropertyMutation,
    useListTemplatePropertiesLazyQuery
} from "../../../__generated__/generated_types";
import { Close, Modal } from "@zendeskgarden/react-modals";
import { toast } from "react-toastify";
import TemplatePropertyForm, { TTemplatePropertyRecord } from "./components/template-property-form/TemplatePropertyForm";
import { ThemeProvider } from "styled-components";
import { ViziblyTheme } from "../../../styles/zendesk-garden/ViziblyZDGTheme";

export interface IEditTemplateProperty {
    templatePropertyId: string | undefined,
    formVisible: boolean,
    budgetYear: number,
    onModalClose: () => void,
    onUpdate: () => void,
}

export function TemplatePropertyEditModal(props: IEditTemplateProperty): ReactElement {
    const {
        templatePropertyId,
        formVisible,
        onModalClose,
        onUpdate,
    } = props;

    const [loadTemplateProperties, { data: templatePropertiesData, loading: templatePropertiesLoading }] = useListTemplatePropertiesLazyQuery({
        fetchPolicy: "no-cache",
        variables: {
            includeDeactivated: true,
            budgetYear: props.budgetYear
        }
    });

    const [updateTemplateProperty, { loading: updateLoading }] = useUpdateTemplatePropertyMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.updateTemplateProperty) {
                toast.success(`Template property "${data.updateTemplateProperty.name}" updated`);
                onUpdate();
            }
            reset();
        },
    });

    const [templateProperty, setTemplateProperty] = useState<TTemplatePropertyRecord | undefined>();

    useEffect(() => {
        if (!templatePropertyId || !formVisible) {
            return;
        }

        loadTemplateProperties();
    }, [templatePropertyId, formVisible]);

    useEffect(() => {
        if (!templatePropertiesLoading && templatePropertiesData?.listTemplateProperties) {
            const foundProperty = templatePropertiesData.listTemplateProperties.find(
                property => property.id === templatePropertyId
            );

            if (foundProperty) {
                setTemplateProperty({
                    id: foundProperty.id,
                    name: foundProperty.name,
                    isDeactivated: foundProperty.isDeactivated,
                    reforecastStartMonthIndex: foundProperty.reforecastStartMonthIndex,
                    reforecastYear: foundProperty.reforecastYear
                });
            }
        }
    }, [templatePropertiesLoading, templatePropertiesData, templatePropertyId]);

    function updateTemplatePropertyState(update: Partial<TTemplatePropertyRecord>): void {
        if (templateProperty) {
            setTemplateProperty({
                ...templateProperty,
                ...update,
            });
        }
    }

    function reset(): void {
        setTemplateProperty(undefined);
        onModalClose();
    }

    function doSaveTemplateProperty(): void {
        if (templateProperty && templateProperty.id) {
            updateTemplateProperty({
                variables: {
                    id: templateProperty.id,
                    name: templateProperty.name,
                    isDeactivated: templateProperty.isDeactivated,
                    reforecastStartMonthIndex: templateProperty.reforecastStartMonthIndex,
                    budgetYear: props.budgetYear
                }
            });
        }
    }

    if (!formVisible) {
        return <></>;
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Modal onClose={reset}>
                {
                    templateProperty &&
                    <TemplatePropertyForm
                        templateProperty={templateProperty}
                        loading={updateLoading}
                        onChange={updateTemplatePropertyState}
                        onSubmit={doSaveTemplateProperty}
                        onClose={reset}
                    />
                }
                <Close aria-label="Close modal" />
            </Modal>
        </ThemeProvider>
    );
}
